import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { resolveImg, formatApiErrorDetail } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { ImageUploader } from "@/components/ImageUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const inputCls = "w-full border border-input bg-white px-4 py-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-wine";
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = { name: "", slug: "", subcategories: "", image: "", sort_order: 0 };

export const AdminCategories = () => {
  const { categories, reload } = useCategories();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, slug: c.slug, subcategories: (c.subcategories || []).join(", "), image: c.image || "", sort_order: c.sort_order }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required."); return; }
    setSaving(true);
    const payload = {
      name: form.name,
      slug: form.slug || slugify(form.name),
      subcategories: form.subcategories.split(",").map((s) => s.trim()).filter(Boolean),
      image: form.image || "",
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editing) { await api.put(`/categories/${editing.id}`, payload); toast.success("Category updated."); }
      else { await api.post("/categories", payload); toast.success("Category added."); }
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/categories/${toDelete.id}`); toast.success("Category removed."); setToDelete(null); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-categories">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-stone-900">Categories</h2>
          <p className="mt-1 text-xs text-stone-400">{categories.length} categories</p>
        </div>
        <button data-testid="add-category-btn" onClick={openNew} className="flex items-center gap-2 bg-wine px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-wine-hover">
          <Plus size={15} /> Add category
        </button>
      </div>

      <div className="border border-border">
        <table className="w-full text-left" data-testid="categories-table">
          <thead className="border-b border-border bg-secondary text-[10px] uppercase tracking-[0.2em] text-stone-500">
            <tr>
              <th className="px-5 py-4 font-normal">Name</th>
              <th className="hidden px-5 py-4 font-normal md:table-cell">Slug</th>
              <th className="px-5 py-4 font-normal">Subcategories</th>
              <th className="px-5 py-4 text-right font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`cat-row-${c.slug}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    {c.image ? <img src={resolveImg(c.image)} alt="" className="h-10 w-10 object-cover" /> : <div className="h-10 w-10 bg-secondary" />}
                    <span className="font-serif text-base text-stone-900">{c.name}</span>
                  </div>
                </td>
                <td className="hidden px-5 py-4 text-sm text-stone-400 md:table-cell">{c.slug}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-1.5">
                    {c.subcategories?.length ? c.subcategories.map((s) => (
                      <span key={s} className="border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-stone-500">{s}</span>
                    )) : <span className="text-xs text-stone-300">—</span>}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-cat-${c.slug}`} onClick={() => openEdit(c)} className="border border-border p-2 text-stone-500 hover:border-wine hover:text-wine"><Pencil size={14} /></button>
                    <button data-testid={`delete-cat-${c.slug}`} onClick={() => setToDelete(c)} className="border border-border p-2 text-stone-500 hover:border-red-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="category-form-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">{editing ? "Edit category" : "New category"}</DialogTitle><DialogDescription className="sr-only">Category details form</DialogDescription></DialogHeader>
          <div className="space-y-5 pt-2">
            <Field label="Name"><input data-testid="cat-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} className={inputCls} /></Field>
            <Field label="Slug (URL)"><input data-testid="cat-form-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className={inputCls} placeholder="auto-generated" /></Field>
            <Field label="Tile image (shown on the homepage)">
              <ImageUploader images={form.image ? [form.image] : []} onChange={(imgs) => setForm({ ...form, image: imgs.length ? imgs[imgs.length - 1] : "" })} />
            </Field>
            <Field label="Subcategories (comma separated)"><input data-testid="cat-form-subs" value={form.subcategories} onChange={(e) => setForm({ ...form, subcategories: e.target.value })} className={inputCls} placeholder="e.g. Studs, Hoops, Drops" /></Field>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setOpen(false)} className="border border-border px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900">Cancel</button>
            <button data-testid="save-category-btn" onClick={save} disabled={saving} className="bg-wine px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-wine-hover disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif font-normal">Remove this category?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" will be removed. Products in it will remain but become uncategorised.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-category" onClick={remove} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</label>
    {children}
  </div>
);
